import {useMap} from '@/hooks/useMap';
import {DEFAULT_MAP_ZOOM} from '@/utils/geoBounds';
import {type FC, useEffect, useRef} from 'react';
import mapboxgl from 'mapbox-gl';
type LngLatLike = mapboxgl.LngLatLike;
type MapOptions = mapboxgl.MapOptions;
type Marker = mapboxgl.Marker;
import {animate} from 'motion';
import {colors} from '@/styles/colors';
import {useClusteredMarkers, type MarkerInput} from './useClusteredMarkers';
import {type MapEvent} from '@/apiTypes/event';

export interface FitBoundsPadding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const DEFAULT_FIT_BOUNDS_PADDING: FitBoundsPadding = {
  top: 50,
  bottom: 50,
  left: 50,
  right: 50,
};

export interface MarkerHoverPosition {
  id: string;
  x: number;
  y: number;
  markerHeight: number;
  mapBounds: DOMRect;
}

interface MiniMapProps {
  markers: MarkerInput[];
  /** Ghost events from pages beyond page 1, rendered as small background dots */
  ghostEvents?: MapEvent[];
  onMarkerClick?: (id: string) => void;
  onMarkerHover?: (id: string) => void;
  onMarkerHoverExit?: (id: string) => void;
  /** Called on desktop marker hover with screen position info for popup placement */
  onMarkerHoverWithPosition?: (position: MarkerHoverPosition | null) => void;
  onMapMove?: (sw: LngLatLike, ne: LngLatLike) => void;
  /** Called when the user taps an empty area of the map (not a marker or cluster) */
  onEmptyMapClick?: () => void;
  highlightMarkerId?: string | null;
  options?: Omit<MapOptions, 'container'>;
  /** Increment to programmatically fit the map to all current markers */
  fitToMarkersKey?: number;
  /**
   * Padding (in pixels) applied when fitting the map to marker bounds.
   * Use this to account for UI overlays like the bottom sheet on mobile.
   */
  fitBoundsPadding?: FitBoundsPadding;
  /** Marker ID to center the map on. Map flies to the marker's coordinates (same zoom) when this changes. */
  centerOnMarkerId?: string | null;
}

interface ClusterMarkerEntry {
  marker: Marker;
  markerIds: string[];
}

/** Priority for tie-breaking when multiple event types have equal counts in a cluster. */
const CLUSTER_TYPE_PRIORITY: Record<string, number> = {
  race: 3,
  ride: 2,
  route: 1,
};

/**
 * Returns the dominant event-type color for a cluster using plurality voting.
 * Ties are broken by CLUSTER_TYPE_PRIORITY (race > ride > route).
 */
function getClusterColor(
  markerIds: string[],
  eventTypeMap: Map<string, string>
): string {
  const counts: Record<string, number> = {};
  for (const id of markerIds) {
    const type = eventTypeMap.get(id);
    if (type) counts[type] = (counts[type] ?? 0) + 1;
  }
  let winner: string | null = null;
  let winnerCount = 0;
  for (const [type, count] of Object.entries(counts)) {
    const priority = CLUSTER_TYPE_PRIORITY[type] ?? 0;
    const winnerPriority =
      winner !== null ? (CLUSTER_TYPE_PRIORITY[winner] ?? 0) : -1;
    if (
      count > winnerCount ||
      (count === winnerCount && priority > winnerPriority)
    ) {
      winner = type;
      winnerCount = count;
    }
  }
  return winner !== null
    ? (EVENT_TYPE_COLORS[winner] ?? colors.neutral)
    : colors.neutral;
}

/**
 * Creates a DOM element for a cluster marker that displays the point count.
 * Size scales with the number of contained points.
 */
function createClusterMarkerElement(
  pointCount: number,
  color: string
): HTMLDivElement {
  const size = pointCount < 10 ? 32 : pointCount < 25 ? 38 : 44;

  const el = document.createElement('div');
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.borderRadius = '50%';
  el.style.backgroundColor = color;
  el.style.border = `2px solid ${colors.light}`;
  el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.cursor = 'pointer';

  const label = document.createElement('span');
  label.textContent = String(pointCount);
  label.style.color = colors.light;
  label.style.fontSize = size < 38 ? '12px' : '14px';
  label.style.fontWeight = '700';
  label.style.lineHeight = '1';
  label.style.userSelect = 'none';

  el.appendChild(label);
  return el;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  race: colors.primary,
  ride: colors.secondary,
  route: '#16A34A',
};

/**
 * Formats a date as a short label like "Mar 22".
 */
function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
}

/**
 * Creates a pill-shaped DOM element for a co-located event marker
 * showing the event type badge and date.
 */
function createPillMarkerElement(
  eventType: string,
  startDate: Date | null
): HTMLDivElement {
  const el = document.createElement('div');
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.gap = '6px';
  el.style.backgroundColor = colors.light;
  el.style.borderRadius = '999px';
  el.style.padding = '4px 10px 4px 4px';
  el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
  el.style.cursor = 'pointer';
  el.style.whiteSpace = 'nowrap';

  const badge = document.createElement('div');
  const badgeSize = 22;
  badge.style.width = `${badgeSize}px`;
  badge.style.height = `${badgeSize}px`;
  badge.style.borderRadius = '50%';
  badge.style.backgroundColor = EVENT_TYPE_COLORS[eventType] ?? colors.neutral;
  badge.style.display = 'flex';
  badge.style.alignItems = 'center';
  badge.style.justifyContent = 'center';
  badge.style.flexShrink = '0';

  const badgeLabel = document.createElement('span');
  badgeLabel.textContent = (eventType?.[0] ?? '?').toUpperCase();
  badgeLabel.style.color = '#fff';
  badgeLabel.style.fontSize = '11px';
  badgeLabel.style.fontWeight = '700';
  badgeLabel.style.lineHeight = '1';

  badge.appendChild(badgeLabel);
  el.appendChild(badge);

  const dateLabel = document.createElement('span');
  dateLabel.textContent = startDate ? formatShortDate(startDate) : 'TBD';
  dateLabel.style.fontSize = '13px';
  dateLabel.style.fontWeight = '600';
  dateLabel.style.color = colors.dark;
  dateLabel.style.lineHeight = '1';

  el.appendChild(dateLabel);
  return el;
}

const PILL_HEIGHT = 30;
const PILL_GAP = 4;

/**
 * Removes all Mapbox markers from a ref object, ignoring errors during HMR.
 */
function clearMarkerRef(ref: {[key: string]: Marker}) {
  for (const marker of Object.values(ref)) {
    try {
      marker.remove();
    } catch {
      // Ignore cleanup errors during HMR
    }
  }
}

/**
 * Removes all Mapbox cluster marker entries from a ref object.
 */
function clearClusterRef(ref: {[key: string]: ClusterMarkerEntry}) {
  for (const entry of Object.values(ref)) {
    try {
      entry.marker.remove();
    } catch {
      // Ignore cleanup errors during HMR
    }
  }
}

/**
 * Creates a small 8px ghost dot DOM element colored by event type.
 * Ghost dots sit behind full markers in the DOM and represent events
 * beyond the first page of results.
 */
function createGhostDotElement(eventType: string): HTMLDivElement {
  const baseColor = EVENT_TYPE_COLORS[eventType] ?? colors.neutral;

  // Transparent 44×44px wrapper expands the touch target to Apple's
  // recommended minimum without changing the visual size of the dot.
  const wrapper = document.createElement('div');
  wrapper.style.width = '44px';
  wrapper.style.height = '44px';
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';
  wrapper.style.cursor = 'pointer';
  // Store base color on the wrapper so the highlight effect can reference
  // it from marker.getElement() without needing the ghost events array.
  wrapper.dataset.baseColor = baseColor;

  const dot = document.createElement('div');
  dot.style.width = '8px';
  dot.style.height = '8px';
  dot.style.borderRadius = '50%';
  dot.style.backgroundColor = baseColor;
  dot.style.transition = 'box-shadow 150ms ease';
  dot.style.pointerEvents = 'none';

  wrapper.appendChild(dot);
  return wrapper;
}

export const MiniMap: FC<MiniMapProps> = ({
  markers,
  ghostEvents,
  onMarkerClick,
  onMarkerHover,
  onMarkerHoverExit,
  onMarkerHoverWithPosition,
  onMapMove,
  onEmptyMapClick,
  highlightMarkerId,
  options,
  fitToMarkersKey,
  fitBoundsPadding = DEFAULT_FIT_BOUNDS_PADDING,
  centerOnMarkerId,
}) => {
  const {ref, map} = useMap(options);
  const markersRef = useRef<{[id: string]: Marker}>({});
  const clusterMarkersRef = useRef<{[clusterId: string]: ClusterMarkerEntry}>(
    {}
  );
  const ghostMarkersRef = useRef<{[id: string]: Marker}>({});
  const hoveredMarkerIdRef = useRef<string | null>(null);
  const highlightMarkerIdRef = useRef(highlightMarkerId);
  highlightMarkerIdRef.current = highlightMarkerId;
  const pillMarkerIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedBoundsRef = useRef<boolean>(false);
  const fitBoundsPaddingRef = useRef(fitBoundsPadding);
  fitBoundsPaddingRef.current = fitBoundsPadding;

  const {renderItems, getClusterExpansionZoom} = useClusteredMarkers(
    markers,
    map
  );

  // Set up map move event listener
  useEffect(() => {
    if (!map || !onMapMove) return;

    const handleMapMove = (
      e: mapboxgl.MapEventType['moveend'] & mapboxgl.EventData
    ) => {
      if (!e.originalEvent?.programmatic) {
        const bounds = map.getBounds();
        if (!bounds) return;
        onMapMove(bounds.getSouthWest(), bounds.getNorthEast());
      }
    };

    map.on('moveend', handleMapMove);

    return () => {
      map.off('moveend', handleMapMove);
    };
  }, [map, onMapMove]);

  // Clear tooltip when zoom changes (markers may cluster/uncluster)
  useEffect(() => {
    if (!map || !onMarkerHoverWithPosition) return;

    const handleZoom = () => {
      onMarkerHoverWithPosition(null);
      hoveredMarkerIdRef.current = null;
    };

    map.on('zoomstart', handleZoom);
    return () => {
      map.off('zoomstart', handleZoom);
    };
  }, [map, onMarkerHoverWithPosition]);

  // Fire callback when the user clicks an empty area of the map (not a marker/cluster)
  useEffect(() => {
    if (!map || !onEmptyMapClick) return;

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const target = e.originalEvent.target;
      // Only fire when clicking the canvas itself, not a marker/cluster DOM element
      if (target instanceof HTMLCanvasElement) {
        onEmptyMapClick();
      }
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onEmptyMapClick]);

  // Render ghost dot markers (events beyond page 1) behind full markers.
  // This effect MUST run before the renderItems effect so ghost dots are
  // inserted into the DOM first, placing them at a lower z-order.
  useEffect(() => {
    if (!map) return;

    // Check if map container is still valid (HMR safety)
    try {
      const container = map.getContainer();
      if (!container || !container.parentNode) {
        return;
      }
    } catch {
      return;
    }

    clearMarkerRef(ghostMarkersRef.current);
    ghostMarkersRef.current = {};

    const newGhostMarkers: {[id: string]: Marker} = {};

    for (const event of ghostEvents ?? []) {
      try {
        const el = createGhostDotElement(event.eventType);

        const mbMarker = new mapboxgl.Marker({element: el, occludedOpacity: 1})
          .setLngLat([event.lng, event.lat])
          .addTo(map);

        if (onMarkerClick) {
          el.addEventListener('click', () => {
            onMarkerClick(event.id);
          });
        }

        if (onMarkerHover || onMarkerHoverWithPosition) {
          el.addEventListener('mouseenter', () => {
            hoveredMarkerIdRef.current = event.id;
            // Don't change backgroundColor — the popup is the hover indicator.
            // Changing color causes an orange flash on mobile touch.
            if (onMarkerHover) {
              onMarkerHover(event.id);
            }
            if (onMarkerHoverWithPosition) {
              // Use the inner dot's bounds for accurate popup placement.
              const dot = el.firstElementChild;
              const rect =
                dot instanceof HTMLElement
                  ? dot.getBoundingClientRect()
                  : el.getBoundingClientRect();
              const mapContainer = map.getContainer();
              const mapRect = mapContainer.getBoundingClientRect();
              onMarkerHoverWithPosition({
                id: event.id,
                x: rect.left + rect.width / 2,
                y: rect.top,
                markerHeight: rect.height,
                mapBounds: mapRect,
              });
            }
          });
        }

        if (onMarkerHoverExit || onMarkerHoverWithPosition) {
          el.addEventListener('mouseleave', () => {
            hoveredMarkerIdRef.current = null;
            const dot = el.firstElementChild;
            if (dot instanceof HTMLElement) {
              dot.style.backgroundColor =
                el.dataset.baseColor ?? colors.neutral;
            }
            if (onMarkerHoverExit) {
              onMarkerHoverExit(event.id);
            }
            if (onMarkerHoverWithPosition) {
              onMarkerHoverWithPosition(null);
            }
          });
        }

        newGhostMarkers[event.id] = mbMarker;
      } catch (error) {
        console.warn(`Failed to create ghost marker for ${event.id}:`, error);
      }
    }

    ghostMarkersRef.current = newGhostMarkers;

    return () => {
      clearMarkerRef(ghostMarkersRef.current);
      ghostMarkersRef.current = {};
    };
  }, [
    map,
    ghostEvents,
    onMarkerClick,
    onMarkerHover,
    onMarkerHoverExit,
    onMarkerHoverWithPosition,
  ]);

  // Create/update markers and clusters when renderItems change
  useEffect(() => {
    if (!map) return;

    // Check if map container is still valid (HMR safety)
    try {
      const container = map.getContainer();
      if (!container || !container.parentNode) {
        console.warn('Map container is invalid, skipping marker update');
        return;
      }
    } catch (error) {
      console.warn('Failed to validate map container:', error);
      return;
    }

    // Animate old pill markers out, then remove them after animation.
    // Remove them from markersRef so the diff below doesn't touch them.
    for (const id of pillMarkerIdsRef.current) {
      const marker = markersRef.current[id];
      if (!marker) continue;
      delete markersRef.current[id];
      try {
        const el = marker.getElement();
        el.style.pointerEvents = 'none';
        el.dataset.animatingOut = 'true';
        animate(el, {opacity: 0}, {duration: 0.15, ease: 'easeOut'}).then(() =>
          marker.remove()
        );
      } catch {
        marker.remove();
      }
    }
    pillMarkerIdsRef.current = new Set();

    // Diff clusters: key by sorted marker IDs so identity survives
    // Supercluster clusterId changes. Remove stale, keep matching.
    const clusterKey = (ids: string[]) => [...ids].sort().join(',');
    const nextClusterKeys = new Set<string>();
    for (const item of renderItems) {
      if (item.type === 'cluster') {
        nextClusterKeys.add(clusterKey(item.markerIds));
      }
    }
    // Build lookup from stable key → existing cluster entry
    const existingByKey: {
      [key: string]: {refKey: string; entry: ClusterMarkerEntry};
    } = {};
    for (const [refKey, entry] of Object.entries(clusterMarkersRef.current)) {
      existingByKey[clusterKey(entry.markerIds)] = {refKey, entry};
    }
    // Remove clusters no longer in renderItems
    for (const [key, {refKey, entry}] of Object.entries(existingByKey)) {
      if (!nextClusterKeys.has(key)) {
        entry.marker.remove();
        delete clusterMarkersRef.current[refKey];
      }
    }

    // Diff individual markers: keep existing, add new, remove stale
    const nextMarkerIds = new Set<string>();
    for (const item of renderItems) {
      if (item.type === 'marker') {
        nextMarkerIds.add(item.id);
      } else if (item.type === 'colocated') {
        for (const m of item.markers) {
          nextMarkerIds.add(m.id);
        }
      }
    }

    // Remove markers no longer in renderItems
    for (const id of Object.keys(markersRef.current)) {
      if (!nextMarkerIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
      }
    }

    const newMarkers: {[id: string]: Marker} = {...markersRef.current};
    const newClusters: {[clusterId: string]: ClusterMarkerEntry} = {
      ...clusterMarkersRef.current,
    };

    const markerEventTypeMap = new Map(
      markers.map((m) => [m.id, m.eventType ?? 'ride'])
    );

    for (const item of renderItems) {
      try {
        if (item.type === 'marker') {
          // Skip if this marker already exists on the map
          if (newMarkers[item.id]) continue;

          const baseColor = EVENT_TYPE_COLORS[item.eventType] ?? colors.neutral;
          const markerEl = document.createElement('div');
          markerEl.style.width = '20px';
          markerEl.style.height = '20px';
          markerEl.style.borderRadius = '50%';
          markerEl.style.border = `2px solid ${colors.light}`;
          markerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          markerEl.style.backgroundColor = baseColor;
          markerEl.style.transition = 'box-shadow 150ms ease';
          markerEl.dataset.baseColor = baseColor;

          const mbMarker = new mapboxgl.Marker({element: markerEl, occludedOpacity: 1})
            .setLngLat([item.lng, item.lat])
            .addTo(map);

          if (onMarkerClick || onMarkerHover || onMarkerHoverExit) {
            markerEl.style.cursor = 'pointer';
          }

          if (onMarkerClick) {
            markerEl.addEventListener('click', () => {
              onMarkerClick(item.id);
            });
          }

          if (onMarkerHover || onMarkerHoverWithPosition) {
            markerEl.addEventListener('mouseenter', () => {
              hoveredMarkerIdRef.current = item.id;
              // Only apply hover ring on desktop — iOS synthesizes mouseenter
              // after touch gestures and would re-highlight a closed marker.
              if (window.innerWidth >= 1024) {
                markerEl.style.boxShadow = `0 0 0 2px white, 0 0 0 4px ${baseColor}, 0 2px 4px rgba(0,0,0,0.3)`;
              }
              if (onMarkerHover) {
                onMarkerHover(item.id);
              }
              if (onMarkerHoverWithPosition) {
                const rect = markerEl.getBoundingClientRect();
                const mapContainer = map.getContainer();
                const mapRect = mapContainer.getBoundingClientRect();
                onMarkerHoverWithPosition({
                  id: item.id,
                  x: rect.left + rect.width / 2,
                  y: rect.top,
                  markerHeight: rect.height,
                  mapBounds: mapRect,
                });
              }
            });
          }

          if (onMarkerHoverExit || onMarkerHoverWithPosition) {
            markerEl.addEventListener('mouseleave', () => {
              hoveredMarkerIdRef.current = null;
              const isHighlighted = item.id === highlightMarkerIdRef.current;
              markerEl.style.boxShadow = isHighlighted
                ? `0 0 0 2px white, 0 0 0 4px ${baseColor}, 0 2px 4px rgba(0,0,0,0.3)`
                : '0 2px 4px rgba(0,0,0,0.3)';
              if (onMarkerHoverExit) {
                onMarkerHoverExit(item.id);
              }
              if (onMarkerHoverWithPosition) {
                onMarkerHoverWithPosition(null);
              }
            });
          }

          newMarkers[item.id] = mbMarker;
        } else if (item.type === 'cluster') {
          // Skip if a cluster with the same markers already exists
          const key = clusterKey(item.markerIds);
          const existing = existingByKey[key];
          if (existing) {
            if (existing.refKey !== String(item.clusterId)) {
              delete newClusters[existing.refKey];
            }
            newClusters[String(item.clusterId)] = existing.entry;
            continue;
          }

          const clusterColor = getClusterColor(
            item.markerIds,
            markerEventTypeMap
          );
          const clusterEl = createClusterMarkerElement(
            item.pointCount,
            clusterColor
          );

          const mbMarker = new mapboxgl.Marker({element: clusterEl, occludedOpacity: 1})
            .setLngLat([item.lng, item.lat])
            .addTo(map);

          if (onMarkerHoverWithPosition) {
            clusterEl.addEventListener('mouseenter', () => {
              onMarkerHoverWithPosition(null);
            });
          }

          const clusterId = item.clusterId;
          clusterEl.addEventListener('click', () => {
            const expansionZoom = getClusterExpansionZoom(clusterId);
            map.flyTo({
              center: [item.lng, item.lat],
              zoom: Math.min(expansionZoom, 20),
              duration: 500,
              eventData: {programmatic: true},
            });
          });

          newClusters[String(clusterId)] = {
            marker: mbMarker,
            markerIds: item.markerIds,
          };
        } else if (item.type === 'colocated') {
          const totalHeight =
            item.markers.length * PILL_HEIGHT +
            (item.markers.length - 1) * PILL_GAP;

          const centerIdx = (item.markers.length - 1) / 2;

          for (let i = 0; i < item.markers.length; i++) {
            const m = item.markers[i];
            const distFromCenter = Math.ceil(Math.abs(i - centerIdx));
            const pillEl = createPillMarkerElement(m.eventType, m.startDate);

            // Entrance animation: fade in staggered from center outward
            pillEl.style.opacity = '0';
            animate(
              pillEl,
              {opacity: 1},
              {duration: 0.2, delay: distFromCenter * 0.04, ease: 'easeOut'}
            );

            // Center the stack on the coordinate point:
            // offset each pill from the vertical midpoint of the group
            const yOffset =
              i * (PILL_HEIGHT + PILL_GAP) - (totalHeight - PILL_HEIGHT) / 2;

            const mbMarker = new mapboxgl.Marker({
              element: pillEl,
              offset: [0, yOffset],
              occludedOpacity: 1,
            })
              .setLngLat([item.lng, item.lat])
              .addTo(map);

            if (onMarkerClick) {
              pillEl.addEventListener('click', () => {
                onMarkerClick(m.id);
              });
            }

            if (onMarkerHover || onMarkerHoverWithPosition) {
              pillEl.addEventListener('mouseenter', () => {
                hoveredMarkerIdRef.current = m.id;
                pillEl.style.backgroundColor = '#D4CBC2';
                if (onMarkerHover) {
                  onMarkerHover(m.id);
                }
                if (onMarkerHoverWithPosition) {
                  const rect = pillEl.getBoundingClientRect();
                  const mapContainer = map.getContainer();
                  const mapRect = mapContainer.getBoundingClientRect();
                  onMarkerHoverWithPosition({
                    id: m.id,
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                    markerHeight: rect.height,
                    mapBounds: mapRect,
                  });
                }
              });
            }

            if (onMarkerHoverExit || onMarkerHoverWithPosition) {
              pillEl.addEventListener('mouseleave', () => {
                hoveredMarkerIdRef.current = null;
                pillEl.style.backgroundColor = colors.light;
                if (onMarkerHoverExit) {
                  onMarkerHoverExit(m.id);
                }
                if (onMarkerHoverWithPosition) {
                  onMarkerHoverWithPosition(null);
                }
              });
            }

            newMarkers[m.id] = mbMarker;
            pillMarkerIdsRef.current.add(m.id);
          }
        }
      } catch (error) {
        const itemId =
          item.type === 'marker'
            ? item.id
            : item.type === 'cluster'
              ? `cluster-${item.clusterId}`
              : `colocated-${item.lat},${item.lng}`;
        console.warn(`Failed to create marker for ${itemId}:`, error);
      }
    }

    markersRef.current = newMarkers;
    clusterMarkersRef.current = newClusters;

    // Fit map bounds to show all pins only on initial load
    try {
      if (!hasInitializedBoundsRef.current && markers.length > 0) {
        if (markers.length > 1) {
          const bounds = new mapboxgl.LngLatBounds();
          for (const marker of markers) {
            bounds.extend([marker.lng, marker.lat]);
          }
          map.fitBounds(bounds, {
            padding: fitBoundsPaddingRef.current,
            eventData: {programmatic: true},
          });
        } else if (markers.length === 1) {
          map.setCenter([markers[0].lng, markers[0].lat], {
            eventData: {programmatic: true},
          });
          map.setZoom(DEFAULT_MAP_ZOOM, {eventData: {programmatic: true}});
        }
        hasInitializedBoundsRef.current = true;
      }
    } catch (error) {
      console.warn('Failed to adjust map bounds:', error);
    }

    // No cleanup between runs — the diff logic above handles add/remove.
  }, [
    renderItems,
    map,
    onMarkerClick,
    onMarkerHover,
    onMarkerHoverExit,
    onMarkerHoverWithPosition,
    getClusterExpansionZoom,
    markers,
  ]);

  // Remove all markers from the map on unmount
  useEffect(() => {
    return () => {
      clearMarkerRef(markersRef.current);
      markersRef.current = {};
      clearClusterRef(clusterMarkersRef.current);
      clusterMarkersRef.current = {};
    };
  }, []);

  // Keep a ref to markers so the fitToMarkersKey effect reads the latest
  // markers without re-running every time they change.
  const markersDataRef = useRef(markers);
  markersDataRef.current = markers;

  // Fit map to all markers when fitToMarkersKey changes
  useEffect(() => {
    if (!map || !fitToMarkersKey) return;
    const currentMarkers = markersDataRef.current;
    if (!currentMarkers.length) return;

    try {
      if (currentMarkers.length > 1) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const marker of currentMarkers) {
          bounds.extend([marker.lng, marker.lat]);
        }
        map.fitBounds(bounds, {
          padding: fitBoundsPaddingRef.current,
          duration: 2500,
          eventData: {programmatic: true},
        });
      } else {
        map.flyTo({
          center: [currentMarkers[0].lng, currentMarkers[0].lat],
          zoom: DEFAULT_MAP_ZOOM,
          duration: 2500,
          padding: fitBoundsPaddingRef.current,
          eventData: {programmatic: true},
        });
      }
    } catch (error) {
      console.warn('Failed to fit map to markers:', error);
    }
  }, [fitToMarkersKey, map]);

  // Fly to the marker specified by centerOnMarkerId (same zoom level)
  useEffect(() => {
    if (!map || !centerOnMarkerId) return;
    const markerData =
      markersDataRef.current.find((m) => m.id === centerOnMarkerId) ??
      ghostEvents?.find((e) => e.id === centerOnMarkerId);
    if (!markerData) return;

    map.flyTo({
      center: [markerData.lng, markerData.lat],
      duration: 500,
      eventData: {programmatic: true},
    });
  }, [centerOnMarkerId, map, ghostEvents]);

  // Update marker colors when highlight changes (from event card hover)
  useEffect(() => {
    // On mobile, iOS fires a delayed mouseenter after touch, leaving
    // hoveredMarkerIdRef set when the detail sheet closes. Clear it here so
    // the "isHovered" check below doesn't keep a marker highlighted.
    if (!highlightMarkerId) {
      hoveredMarkerIdRef.current = null;
    }

    // Update individual dot markers (pill markers manage their own hover style)
    for (const [id, marker] of Object.entries(markersRef.current)) {
      try {
        if (pillMarkerIdsRef.current.has(id)) continue;

        const $marker = marker.getElement();
        if (!$marker) continue;

        const isHighlighted = id === highlightMarkerId;
        const isHovered = id === hoveredMarkerIdRef.current;
        const baseColor = $marker.dataset.baseColor ?? colors.neutral;
        $marker.style.boxShadow =
          isHighlighted || isHovered
            ? `0 0 0 2px white, 0 0 0 4px ${baseColor}, 0 2px 4px rgba(0,0,0,0.3)`
            : '0 2px 4px rgba(0,0,0,0.3)';
      } catch (error) {
        console.warn(`Failed to update marker ${id}:`, error);
      }
    }

    // Highlight selected ghost dot: white gap + same-color outer ring
    for (const [id, marker] of Object.entries(ghostMarkersRef.current)) {
      try {
        const $wrapper = marker.getElement();
        if (!$wrapper) continue;

        const $dot = $wrapper.firstElementChild;
        if (!($dot instanceof HTMLElement)) continue;

        const isHighlighted = id === highlightMarkerId;
        const baseColor = $wrapper.dataset.baseColor ?? colors.neutral;
        $dot.style.boxShadow = isHighlighted
          ? `0 0 0 2px white, 0 0 0 4px ${baseColor}`
          : 'none';
      } catch {
        // Ignore update errors during HMR
      }
    }

    // Highlight cluster containing the highlighted event
    for (const entry of Object.values(clusterMarkersRef.current)) {
      try {
        const $el = entry.marker.getElement();
        if (!$el) continue;

        const containsHighlighted =
          highlightMarkerId && entry.markerIds.includes(highlightMarkerId);
        $el.style.boxShadow = containsHighlighted
          ? '0 0 0 3px rgba(241,80,37,0.4), 0 2px 6px rgba(0,0,0,0.3)'
          : '0 2px 6px rgba(0,0,0,0.3)';
      } catch {
        // Ignore cluster update errors during HMR
      }
    }
  }, [highlightMarkerId, renderItems]);

  return (
    <div className="w-full h-full">
      <div className="w-full h-full" ref={ref} />
    </div>
  );
};
